import { faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosProgressEvent } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import {
  useDeleteFileMutation,
  useGetFileSizeMutation,
  useMultiUploadFileMutation,
  useUploadFileMutation,
} from '../../Apis/LMSService/uploadApi';
import { convertData, rtkErrorRead, toastNotify } from '../../Helper';
import formatSize from '../../Helper/sizeFormater';
import { apiResponse } from '../../Interfaces';
import { UploadProps } from '../../Interfaces/Props';


const UploadComp: React.FC<UploadProps> = (props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [fileMetadata, setFileMetadata] = useState<
    {
      name: string;
      size: number;
    }[]
  >([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const showPreview = props.showPreview ?? true

  const [uploadFile] = useUploadFileMutation();
  const [multiUploadFile] = useMultiUploadFileMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [getSize] = useGetFileSizeMutation();

  useEffect(() => {
    if (props.previewUrl) {
      const urls: string[] = []
      if (Array.isArray(props.previewUrl)) {
        props.previewUrl.forEach((u) => {
          if (isValidURL(u)) {
            urls.push(u)
          }
        })
      } else {
        if (isValidURL(props.previewUrl)) {
          urls.push(props.previewUrl)
        }
      }
      console.log(urls)
      setPreviewUrls(urls);
    }
    if (props.errorTxt) {
      setErrorText(props.errorTxt);
    }
    setProgress(0);
  }, [props.previewUrl, props.errorTxt]);

  useEffect(() => {
    const fetchFileSizes = async () => {
      const sizes = await Promise.all(previewUrls.map((url) => getSize(url)));
      const updatedMetadata = sizes.map((size, index) => {
        if ('data' in size) {
          return {
            name: fileMetadata[index]?.name || 'File',
            size: size.data || 0, // Set to 0 if size not found
          };
        } else {
          return {
            name: fileMetadata[index]?.name || 'File',
            size: 0, // Set to 0 if size not found
          };
        }
      });
      setFileMetadata(updatedMetadata);
    };

    if (previewUrls.length > 0) {
      fetchFileSizes();
    }
  }, [previewUrls]);

  useEffect(() => {
    return () => {
      setFiles([]);
      setPreviewUrls([]);
      setFileMetadata([]);
      setErrorText('');
      setProgress(0);
    };
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    setFiles(selectedFiles);

    const metadata = selectedFiles.map((file) => ({
      name: file.name,
      size: file.size,
    }));
    setFileMetadata(metadata);
  };

  const handleFileUpload = async () => {
    if (props.disabled) return;

    if (files.length === 0 && props.required) {
      setErrorText(`${props.labelName} is required.`);
      return;
    }

    setIsLoading(true);
    setErrorText('');
    setProgress(0);

    try {
      if (props.documentId) {
        const idsToDelete = Array.isArray(props.documentId)
          ? props.documentId
          : [props.documentId];
        for (const docId of idsToDelete) {
          if (docId !== '0') {
            const deleteResponse: apiResponse = await deleteFile(docId);
            if (deleteResponse.error?.data) {
              toastNotify(
                deleteResponse.error.data.errorMessages.join('<br />'),
                'error'
              );
              setIsLoading(false);
              return;
            }
          }
        }
        props.setDocumentId([]);
        setPreviewUrls([]);
      }

      const IsEncrypt = props?.IsEncrypt ? String(props?.IsEncrypt) : 'false';
      const folderPath = Array.isArray(props.folderPath)
        ? props.folderPath
        : Array.from(
          { length: files.length },
          (_, i) => `${props.folderPath}_${i + 1}`
        );
      const DocumentTitle = Array.isArray(props.DocumentTitle)
        ? props.DocumentTitle
        : Array.from(
          { length: files.length },
          (_, i) => `${props.DocumentTitle}_${i + 1}`
        );
      const formData = new FormData();

      if (props.isMulti) {
        files.forEach((file, index) => {
          formData.append(`multiFiles[${index}].FileDetails`, file);
          formData.append(`multiFiles[${index}].folderPath`, validFolderPath(folderPath[index]) as string);
          formData.append(
            `multiFiles[${index}].DocumentTitle`,
            DocumentTitle[index]
          );
          formData.append(`multiFiles[${index}].IsEncrypt`, IsEncrypt);
        });
        formData.append('key', props.key ?? '');
      } else {
        files.forEach((file) => {
          formData.append('FileDetails', file);
        });
        formData.append('folderPath', validFolderPath(props.folderPath) as string);
        formData.append('DocumentTitle', props.DocumentTitle as string);
        formData.append('key', props.key ?? '');
        formData.append('IsEncrypt', IsEncrypt);
      }

      const uploadProgressCallback = (event: AxiosProgressEvent) => {
        if (event.total) {
          const percentCompleted = Math.round(
            (event.loaded * 100) / event.total
          );
          setProgress(percentCompleted);
        }
      };

      const response: apiResponse = props.isMulti
        ? await multiUploadFile({
          formData,
          onUploadProgress: uploadProgressCallback,
        })
        : await uploadFile({
          formData,
          onUploadProgress: uploadProgressCallback,
        });

      if (response.error?.data) {
        rtkErrorRead(response.error);
      } else {
        const dId = convertData(response?.data?.result);
        const { DocumentId, returnPath } = dId;
        const newDocumentIds = props.isMulti
          ? dId?.map((x: any) => x.DocumentId)
          : DocumentId;
        const newReturnPaths = props.isMulti
          ? dId?.map((x: any) => x.returnPath)
          : returnPath;

        const _previewUrls = Array.isArray(newReturnPaths)
          ? newReturnPaths
          : [newReturnPaths];
        setPreviewUrls(_previewUrls);
        props.setDocumentId(newDocumentIds);
        if (props.setReturnedPath) {
          props.setReturnedPath(newReturnPaths);
        }

        if (props.onUploadComplete) {
          props.onUploadComplete({ id: newDocumentIds, url: newReturnPaths });
        }

        setFiles([]);
        toastNotify('Upload successfully');

        // Clear the input after successful upload
        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Reset the input value
        }
      }
    } catch (error) {
      setErrorText('An error occurred during file upload.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (index: number) => {
    if (props.documentId) {
      setProgress(0);

      const idsToDelete = Array.isArray(props.documentId)
        ? props.documentId
        : [props.documentId];
      const docId = idsToDelete[index];
      if (docId) {
        const response: apiResponse = await deleteFile(docId);
        if (response.error?.data) {
          toastNotify(response.error?.data.errorMessages.join(' '), 'error');
        } else {
          toastNotify('Deleted successfully');
          const ids = idsToDelete.filter((_, i) => i !== index)
          const urls = previewUrls.filter((_, i) => i !== index)
          props.setDocumentId(ids);
          props.setReturnedPath?.(urls);
          props.onUploadComplete?.({ id: !!props.isMulti ? ids : '', url: !!props.isMulti ? urls : '' })
          setPreviewUrls(urls);
        }
      }
    } else {
      toastNotify('No file to delete!', 'error');
    }
  };

  return (
    <div className="w-full">
      {props.labelShow && (
        <label className={props.labelClass}>
          {props.labelName}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="mb-2">
        <div className="flex items-center space-x-4">
          <input
            type="file"
            accept={props.accept}
            className={`form-input ${props.inputClass}`}
            onChange={handleFileInput}
            multiple={props.isMulti}
            disabled={props.disabled}
            ref={fileInputRef}
            required={props.required}
          />

          {!isLoading && (
            <FontAwesomeIcon
              icon={faUpload}
              title="Upload File"
              onClick={handleFileUpload}
              className="text-green-500 cursor-pointer"
            />
          )}
        </div>

        {/* File Previews */}
        {showPreview && previewUrls.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {previewUrls.map((url, index) => {
              const metadata = fileMetadata[index];
              const sizeFormatted =
                metadata?.size !== undefined && metadata?.size !== null
                  ? formatSize(metadata.size)
                  : null;
              return (
                <div
                  key={index}
                  className="relative p-2 border rounded-lg bg-gray-200 shadow-md"
                >
                  {/* Display either image preview or file link */}
                  {props.accept && (props.accept.startsWith('image/') || props.accept.includes('jpeg') || props.accept.includes('png') || props.accept.includes('jpg') || props.accept.includes('webp')) ? (
                    <img
                      src={url}
                      alt="Preview"
                      className={`block object-cover ${props.imgTagClass} w-full h-24`}
                    />
                  ) : (
                    <a
                      target="_blank"
                      href={url}
                      title="View File"
                      className={`block ${props.aTagClass} text-blue-600 underline`}
                      rel="noreferrer"
                    >
                      {props.DocumentTitle || 'View Document'}
                    </a>
                  )}

                  <div className="mt-2 text-sm text-gray-600">
                    <p>{metadata?.name || props.DocumentTitle}</p>
                    {sizeFormatted && <p>{sizeFormatted}</p>}
                  </div>

                  {/* Delete button */}
                  <FontAwesomeIcon
                    icon={faTimes}
                    title="Delete File"
                    onClick={() => handleDelete(index)}
                    className="absolute top-1 right-1 text-red-500 cursor-pointer"
                  />
                </div>
              );
            })}
          </div>
        )}

        {isLoading && progress > 0 && (
          <div
            className={`bg-primary h-4 rounded-full ${progress !== 100 && 'animated-progress'}`}
            style={{
              width: `${progress}%`,
              backgroundImage:
                'linear-gradient(45deg,hsla(0,0%,100%,.15) 25%,transparent 0,transparent 50%,hsla(0,0%,100%,.15) 0,hsla(0,0%,100%,.15) 75%,transparent 0,transparent)',
              backgroundSize: '1rem 1rem',
            }}
          >
            <div className="text-center text-white text-xs">{progress}%</div>
          </div>
        )}

        {errorText && <span className="text-red-500">{errorText}</span>}
      </div>
    </div>
  );
};

function isValidURL(url: string) {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}

// remove unnecessary / from path start 
function validFolderPath(path: string | string[]): string | string[] {
  if (Array.isArray(path)) {
    const newPath = path.map((p) => {
      if (p.startsWith('/')) {
        return p.slice(1)
      } else {
        return p
      }
    })
    return newPath
  } else {
    if (path.startsWith('/')) {
      return path.slice(1)
    } else {
      return path
    }
  }
}


export default UploadComp;

export { isValidURL }
